import { ModuleWrapper } from '@/components'
import { MonthPicker } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { PageType } from '@/modules/common/consts'
import {
  useAdmin,
  useLeaderGroups,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { JSONValue } from '@ur-solutions/react-hooks'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { startOfMonth } from 'date-fns'
import { isDesktop } from 'react-device-detect'
import styled from 'styled-components'
import { InfringementPopupFilter } from '../components'
import { InfringementsTable } from '../components/table/InfringementsTable'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import type { InfringementCategory } from '../types.graphl'

const FilterSection = styled.section`
  grid-area: filters;

  display: flex;
  gap: 1rem;

  & > * {
    max-width: 100%;
  }

  ${props => props.theme.layout.largePad} {
    flex-direction: column;

    & > * {
      width: 100%;
    }
  }

  ${props => props.theme.layout.mobile} {
    flex-direction: column;
  }
`

const SelectUsersWrapper = styled.div`
  overflow: visible;
  width: calc(100% - 340px - 250px - 2rem);
  height: 54px;

  ${props => props.theme.layout.largePad} {
    width: 100%;
  }
`

const AreaInfringementPopupFilter = styled.div`
  display: flex;
  width: 250px;

  & > div {
    width: 100%;
  }

  ${props => props.theme.layout.largePad} {
    width: 100%;

    & > div {
      width: 100%;
    }
  }
  ${props => props.theme.layout.mobile} {
    width: 100%;

    & > div {
      width: 100%;
    }
  }
`

const MonthFlexArea = styled.div`
  display: flex;
  width: 340px;
  gap: 1rem;

  ${props => props.theme.layout.largePad} {
    width: 100%;
  }
`

const Controls = styled(Card)`
  width: 100%;
  box-sizing: border-box;
  overflow-x: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  display: flex;
`
export const Infringements: React.FC = () => {
  const translations = useTranslate({
    infringements: 'common.infringements',
    users: 'common.users',
    user: 'common.user',
    export: 'common.export',
    archived: 'common.archived',
    chooseGroup: 'common.choose-group',

    success: 'toasts.email-sent-successfully',
    error: 'errors.generic-error',
  })

  const admin = useAdmin()
  const me = useUser()
  const { hasPermissions } = usePermissions()

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
  } = useForm<InfringementsTableFilter>({
    values: {
      query: '',
      orderBy: '-infringementTimestamp',
      userOrderBy: '-firstName',
      excludeSeverities: [],
      excludeCategories: [],
      archived: false,
      users: admin ? [] : [me.id],
      month: new Date(),
      leaderGroup: null,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'firmadok__infringements-overview-filter',
        transformValues: {
          orderBy: value => (typeof value === 'string' ? value : undefined),
          userOrderBy: value => (typeof value === 'string' ? value : undefined),
          users: value => {
            if (Array.isArray(value)) {
              return value.filter(val => typeof val === 'string') as string[]
            }
            return []
          },
          leaderGroup: value => (typeof value === 'string' ? value : null),
          excludeSeverities: value => {
            function isSeverityCategory(
              value: JSONValue
            ): value is SeverityCategory {
              return (
                typeof value === 'string' &&
                ['MINOR', 'SERIOUS', 'VERY_SERIOUS', 'MOST_SERIOUS'].includes(
                  value
                )
              )
            }
            if (Array.isArray(value)) {
              return value.filter(val =>
                isSeverityCategory(val)
              ) as SeverityCategory[]
            }
            return []
          },
          excludeCategories: value => {
            if (Array.isArray(value)) {
              return value as InfringementCategory[]
            }
            return []
          },
        },
      },
    },
  })
  const monthStartDate = startOfMonth(filter.month)

  return (
    <ModuleWrapper
      title={
        isDesktop
          ? `${translations.infringements}${
              filter.archived ? ' (' + translations.archived + ')' : ''
            }`
          : translations.infringements
      }
      page={PageType.INFRINGEMENTS}
    >
      <Controls>
        <FilterSection>
          {isDesktop && canViewOthers && (
            <SelectUsersWrapper>
              <SelectMultipleUsers
                minWidth="300px"
                maxWidth="100%"
                selected={filter.users}
                dropdownPageName={DropdownNames.ACTIVITIES}
                placeholder={translations.user}
                onChange={filterHandler('users')}
                filterLeaderGroup={true}
              />
            </SelectUsersWrapper>
          )}

          {hasAccess && (
            <LeaderGroupSelect
              value={filter.leaderGroup}
              loading={groupsLoading}
              options={
                myLeaderGroups?.map(group => ({
                  label: group.name,
                  value: group.id,
                })) ?? []
              }
              fullWidth
              grayed={true}
              onChange={filterHandler('leaderGroup')}
              placeholder={translations.chooseGroup}
              nullable
            />
          )}

          <MonthFlexArea>
            <MonthPicker
              value={monthStartDate}
              disableAfter={true}
              fullWidth
              onChange={filterHandler('month')}
            />
          </MonthFlexArea>

          <AreaInfringementPopupFilter>
            <InfringementPopupFilter filter={filter} onUpdate={updateForm} />
          </AreaInfringementPopupFilter>
        </FilterSection>
      </Controls>

      <InfringementsTable filter={filter} onSort={filterHandler('orderBy')} />
    </ModuleWrapper>
  )
}
